import {AfterViewChecked, ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from 'rxjs';
import {FileModel} from '@core/models/file.model';
import {WikiLinkResolverService} from '@core/services/entities/wiki/wiki-link-resolver.service';

@Component({
  selector: 'content-view',
  templateUrl: './content-view.component.html',
  styleUrls: ['./content-view.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContentViewComponent implements OnInit, OnDestroy, AfterViewChecked {
  private _content: string;
  private _files: FileModel[] = [];
  private _wikiLinkSubs = new Subscription();
  private _resolvedLinkSignature = '';
  private _pendingWikiLinkResolve = false;

  @Input()
  set content(value: string) {
    // Один общий проход: парсим HTML в детач-DOM, расставляем target/rel/loading,
    // и одновременно режем на preview/spoiler. Это в 2 раза быстрее, чем
    // два независимых div.innerHTML = value, и не плодит лишний мусор для GC.
    this._prepareContent(value);

    // Скан внутренних wiki-ссылок запустим один раз в ближайший ngAfterViewChecked,
    // когда innerHTML уже отрисован.
    this._resolvedLinkSignature = '';
    this._pendingWikiLinkResolve = true;
  }

  get content() {
    return this._content;
  }

  @Input()
  set files(value: FileModel[]) {
    this._files = value;
  }

  get files() {
    return this._files;
  }

  @Input() isPreviewMode = false;

  previewContent = '';
  spoilerContent = '';
  isDeployed = false;

  constructor(
    private _elementRef: ElementRef<HTMLElement>,
    private _wikiLinkResolver: WikiLinkResolverService,
  ) {
  }

  ngOnInit() {
    // Angular устанавливает @Input в порядке их объявления, поэтому setter content
    // отрабатывает раньше, чем isPreviewMode = true приходит из родителя. Перезапускаем
    // обработку здесь, когда оба input'а уже установлены. _prepareContent идемпотентен:
    // target/rel/loading на ссылках/img/iframe не дублируются при повторном проходе.
    if (this.isPreviewMode && this._content) {
      this._prepareContent(this._content);
    }
  }

  ngOnDestroy() {
    this._wikiLinkSubs.unsubscribe();
  }

  ngAfterViewChecked() {
    if (!this._pendingWikiLinkResolve) {
      return;
    }
    this._pendingWikiLinkResolve = false;
    this._resolveWikiLinks();
  }

  onChangeDeployed(value) {
    this.isDeployed = value;
    // При раскрытии спойлера в DOM появляется новый кусок контента —
    // запускаем повторный скан.
    this._resolvedLinkSignature = '';
    this._pendingWikiLinkResolve = true;
  }

  /**
   * Один проход парсинга HTML контента вместо двух:
   *  - расставляет target="_blank" + rel у внешних ссылок (внутренние data-wiki-id трогать не надо);
   *  - выставляет loading="lazy" у <img>/<iframe>, чтобы не качать превью невидимых статей;
   *  - в preview-режиме одновременно режет на preview/spoiler по <hr>/.ck-delimiter.
   */
  private _prepareContent(value: string) {
    if (!value) {
      this._content = value;
      this.previewContent = '';
      this.spoilerContent = '';
      return;
    }

    const root = document.createElement('div');
    root.innerHTML = value;

    root.querySelectorAll('a').forEach((link) => {
      if (link.getAttribute('data-wiki-id')) {
        // Внутренние ссылки на статьи открываются роутером, а не новой вкладкой.
        link.removeAttribute('target');
        return;
      }
      link.setAttribute('target', '_blank');

      const existingRel = (link.getAttribute('rel') || '').split(/\s+/).filter(Boolean);
      const relValues = new Set([...existingRel, 'noopener', 'noreferrer']);
      link.setAttribute('rel', Array.from(relValues).join(' '));
    });

    root.querySelectorAll('img, iframe').forEach((media) => {
      if (!media.hasAttribute('loading')) {
        media.setAttribute('loading', 'lazy');
      }
    });

    this._content = root.innerHTML;

    if (!this.isPreviewMode) {
      this.previewContent = '';
      this.spoilerContent = '';
      return;
    }

    const previewContent = document.createElement('div');
    const spoilerContent = document.createElement('div');
    let isPreview = true;

    // Перекладываем childNodes из root напрямую (это перемещает их, не клонирует).
    Array.from(root.childNodes).forEach((node) => {
      const isDelimiter = node instanceof HTMLElement
        && (node.classList.contains('ck-delimiter') || node.tagName === 'HR');

      if (isDelimiter) {
        isPreview = false;
      }

      if (isPreview) {
        previewContent.appendChild(node);
      } else {
        spoilerContent.appendChild(node);
      }
    });

    this.previewContent = previewContent.innerHTML;
    this.spoilerContent = spoilerContent.innerHTML;
  }

  /**
   * Подменяет текст у `a[data-wiki-id]` (и совместимым `a[href]`) на актуальное
   * имя статьи. Подписка живёт до уничтожения компонента, поэтому переименование
   * статьи в сторе автоматически обновляет уже отрисованный контент.
   */
  private _resolveWikiLinks(): void {
    const host = this._elementRef?.nativeElement;
    if (!host) {
      return;
    }

    const allLinks = Array.from(host.querySelectorAll('a')) as HTMLAnchorElement[];
    if (!allLinks.length) {
      return;
    }

    const targets: { id: string; element: HTMLAnchorElement }[] = [];
    allLinks.forEach((link) => {
      let id = link.getAttribute('data-wiki-id');
      if (!id) {
        const parsed = this._wikiLinkResolver.parseInternalUrl(link.getAttribute('href'));
        if (!parsed) {
          return;
        }
        id = parsed.id;
        link.setAttribute('data-wiki-id', id);
        const existingClasses = (link.getAttribute('class') || '').split(/\s+/).filter(Boolean);
        if (!existingClasses.includes('wiki-internal-link')) {
          existingClasses.push('wiki-internal-link');
          link.setAttribute('class', existingClasses.join(' '));
        }
        link.removeAttribute('target');
      } else if (link.getAttribute('target')) {
        // Защитный шаг: если в DOM прокрался target на уже-тегированной ссылке
        // (старый контент, расширения, нормализатор) — снимаем.
        link.removeAttribute('target');
      }
      targets.push({ id, element: link });
    });

    if (!targets.length) {
      return;
    }

    // Сигнатура — список id+href, чтобы не подписываться повторно при каждом
    // change detection, пока контент тот же.
    const signature = targets.map((t) => `${t.id}|${t.element.getAttribute('href') || ''}`).join('\n');
    if (signature === this._resolvedLinkSignature) {
      return;
    }
    this._resolvedLinkSignature = signature;
    this._wikiLinkSubs.unsubscribe();
    this._wikiLinkSubs = new Subscription();

    const grouped = new Map<string, HTMLAnchorElement[]>();
    targets.forEach(({ id, element }) => {
      const list = grouped.get(id) || [];
      list.push(element);
      grouped.set(id, list);
    });

    grouped.forEach((elements, id) => {
      const sub = this._wikiLinkResolver.resolveName(id).subscribe((name) => {
        if (!name) {
          return;
        }
        elements.forEach((el) => {
          if (el.textContent !== name) {
            el.textContent = name;
          }
        });
      });
      this._wikiLinkSubs.add(sub);
    });
  }
}
